import React, { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'umi';
import { Card, Bubble, Segment } from 'tea-component';
import sortdown from '@/pages/intelligence/assets/svg/selectIcon/arrow-bottom.svg';
import SegmentAutoSlider from '@/pages/intelligence/pages/TopCharts/PCGames/components/SliderSegement/SegmentAutoSlider';
import {
  ROBLOX_GAME_RANK_CATEGORIES,
  getRobloxRankSourceDisplayLabel,
  getRobloxRankSourcesByCategory,
} from '../../config/robloxRankSourceConfig';
import { ROBLOX_GAME_RANK_TABLE_SORT } from '../../constants';

import type { TRobloxRankCategory } from '../../config/robloxRankSourceConfig';

import './index.scss';

const SORT_ORDER_ICON_MAP: Record<string, string> = {
  desc: sortdown,
  asc: sortdown,
};

interface IRobloxGameRankSectionTitleProps {
  category: TRobloxRankCategory;
  source: string;
  onCategoryChange: (category: TRobloxRankCategory) => void;
  onSourceChange: (source: string) => void;
  chart?: React.ReactNode;
}

const RobloxGameRankSectionTitle: React.FC<IRobloxGameRankSectionTitleProps> = ({
  category,
  source,
  onCategoryChange,
  onSourceChange,
  chart,
}) => {
  const { formatMessage } = useIntl();
  const { order } = ROBLOX_GAME_RANK_TABLE_SORT;
  const cardRef = useRef<HTMLDivElement>(null);
  const primaryContainerRef = useRef<HTMLDivElement>(null);

  const getCardBodyNode = useCallback((): HTMLElement | null => {
    return cardRef.current?.querySelector('.tea-card__body') as HTMLElement | null;
  }, []);

  const sortFieldLabel = useMemo(() => {
    return getRobloxRankSourceDisplayLabel(source, formatMessage);
  }, [formatMessage, source]);

  const sortedByText = useMemo(() => {
    return formatMessage(
      { id: 'dbi_roblox_game_rank_sorted_by' },
      { sortField: sortFieldLabel },
    );
  }, [formatMessage, sortFieldLabel]);

  const primaryOptions = useMemo(() => {
    return ROBLOX_GAME_RANK_CATEGORIES.map((item) => ({
      value: item.value,
      text: formatMessage({ id: item.labelKey }),
    }));
  }, [formatMessage]);

  const secondaryOptions = useMemo(() => {
    return getRobloxRankSourcesByCategory(category).map((item) => {
      const label = formatMessage({ id: item.labelKey });
      const description = formatMessage({ id: item.descriptionKey });

      return {
        value: item.value,
        text: (
          <Bubble placement="top" dark style={{ maxWidth: 450 }} content={description}>
            <span className="dbi-roblox-game-rank-segment-option">{label}</span>
          </Bubble>
        ),
      };
    });
  }, [category, formatMessage]);

  return (
    <div ref={cardRef} className="dbi-roblox-game-rank-section-card-wrap">
      <Card className="dbi-mobileGames-card-context dbi-roblox-game-rank-section-card">
        <Card.Body
          title={
            <div className="dbi-roblox-game-rank-section-card__title">
              <span className="dbi-roblox-game-rank-section-card__title-text">
                {formatMessage({ id: 'dbi_roblox_game_rank_section_title' })}
              </span>
              <span className="dbi-details-text">
                {sortedByText}
                &nbsp;
                (<img className="dbi-details-text-icon" src={SORT_ORDER_ICON_MAP[order] || sortdown} alt="" />)
              </span>
            </div>
          }
        >
          <div className="dbi-roblox-game-rank-section-card__segments">
            <div
              ref={primaryContainerRef}
              className="dbi-roblox-game-rank-segment-row dbi-roblox-game-rank-segment-row--primary"
            >
              <SegmentAutoSlider
                key={`roblox-rank-primary-${category}`}
                allowBtnDisable
                getParentNode={() => primaryContainerRef.current || getCardBodyNode()}
              >
                <Segment
                  className="dbi-roblox-game-rank-segment"
                  options={primaryOptions}
                  value={category}
                  onChange={(value) => onCategoryChange(value as TRobloxRankCategory)}
                />
              </SegmentAutoSlider>
            </div>

            <div className="dbi-roblox-game-rank-segment-row dbi-roblox-game-rank-segment-row--secondary">
              <SegmentAutoSlider
                key={`roblox-rank-secondary-${category}-${secondaryOptions.length}`}
                allowBtnDisable
                getParentNode={getCardBodyNode}
              >
                <Segment
                  className="dbi-roblox-game-rank-segment"
                  options={secondaryOptions}
                  value={source}
                  onChange={onSourceChange}
                />
              </SegmentAutoSlider>
            </div>
          </div>

          {chart ? (
            <div className="dbi-roblox-game-rank-section-card__chart">
              {chart}
            </div>
          ) : null}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RobloxGameRankSectionTitle;
